// src/lib/duckdb/register.ts
import type { AsyncDuckDBConnection } from "@duckdb/duckdb-wasm";

/* ------------------------------------------------------------------
 * 1.  Mount files into DuckDB’s VFS
 * ------------------------------------------------------------------ */

export async function registerFileBuffer(
  conn: AsyncDuckDBConnection,
  fileName: string,
  buf: Uint8Array
) {
  await conn.bindings.registerFileBuffer(fileName, buf);
}

export async function registerFileURL(
  conn: AsyncDuckDBConnection,
  fileName: string,
  url: string
) {
  await conn.bindings.registerFileURL(fileName, url, "HTTPFS");
}

/* ------------------------------------------------------------------
 * 2.  Table / view builders for each format
 * ------------------------------------------------------------------ */

export async function registerCsv(
  conn: AsyncDuckDBConnection,
  tableName: string,
  filePath: string
) {
  await conn.insertCSVFromPath(filePath, {
    name: tableName,
    create: true,
    header: true,
  });
}

export async function registerParquet(
  conn: AsyncDuckDBConnection,
  tableName: string,
  filePath: string
) {
  await conn.query(
    `CREATE OR REPLACE VIEW "${tableName}" AS SELECT * FROM read_parquet('${filePath}')`
  );
}

export async function registerJson(
  conn: AsyncDuckDBConnection,
  tableName: string,
  filePath: string
) {
  await conn.query(
    `CREATE OR REPLACE VIEW "${tableName}" AS SELECT * FROM read_json_auto('${filePath}')`
  );
}

/* ------------------------------------------------------------------
 * 3.  Drop any existing table/view
 * ------------------------------------------------------------------ */

export async function dropRelation(
  conn: AsyncDuckDBConnection,
  relationName: string
) {
  await conn.query(`DROP VIEW  IF EXISTS "${relationName}"`);
  await conn.query(`DROP TABLE IF EXISTS "${relationName}"`);
}

/* ------------------------------------------------------------------
 * 4.  High-level helper for remote URLs (CSV | Parquet | JSON)
 * ------------------------------------------------------------------ */

export async function registerTableFromUrl(
  conn: AsyncDuckDBConnection,
  tableName: string,
  url: string
) {
  const rawName  = url.split("/").pop() ?? "remote_file";
  const fileName = rawName.split(/[?#]/)[0];
  const ext      = fileName.split(".").pop()?.toLowerCase();

  if (!ext || !["csv", "parquet", "json"].includes(ext)) {
    throw new Error("Unsupported file type (only .csv, .parquet or .json)");
  }

  await dropRelation(conn, tableName);

  /* 1️⃣  mount */
  let useHttpFs = false;
  try {
    const head = await fetch(url, { method: "HEAD" });
    useHttpFs = head.ok && head.headers.get("accept-ranges")?.includes("bytes");
  } catch { /* network failure → fall back to full download */ }

  if (useHttpFs) {
    await registerFileURL(conn, fileName, url);
  } else {
    const resp = await fetch(url);
    if (!resp.ok) {
      throw new Error(`Download failed: ${resp.status} ${resp.statusText}`);
    }
    const buf = new Uint8Array(await resp.arrayBuffer());
    await registerFileBuffer(conn, fileName, buf);
  }

  /* 2️⃣  delegate */
  if (ext === "csv") {
    await registerCsv(conn, tableName, fileName);
  } else if (ext === "parquet") {
    await registerParquet(conn, tableName, fileName);
  } else {
    await registerJson(conn, tableName, fileName);
  }
}
